import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const QuickStatsGrid = ({ selectedClient, onTotalClientsClick }) => {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeItems: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [selectedClient]);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const WORKER_URL = import.meta.env.VITE_WORKER_URL;
      const WORKER_SECRET = import.meta.env.VITE_WORKER_SECRET;

      // Helper to run query via worker
      const runQuery = async (queryStr, paramsArr = []) => {
        const res = await fetch(`${WORKER_URL}/data/query`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Worker-Auth': WORKER_SECRET },
          body: JSON.stringify({ query: queryStr, params: paramsArr })
        });
        const rows = await res.json();
        // Since we fixed the unwrap logic, the worker might return an object with `.rows` or just the array directly
        const resultRows = (rows && !Array.isArray(rows) && Array.isArray(rows?.rows)) ? rows.rows : rows;
        return Array.isArray(resultRows) ? resultRows : [];
      };

      // Fetch total clients count
      const clientRows = await runQuery(`SELECT COUNT(*) as count FROM public.lasea_users WHERE role = 'customer'`);
      const clientCount = parseInt(clientRows[0]?.count || 0, 10);

      // Fetch active inventory items
      let inventoryCount = 0;
      if (selectedClient?.id) {
        const inventoryRows = await runQuery(`SELECT COUNT(*) as count FROM lasea_inventory WHERE is_active = true AND client_id = $1`, [selectedClient.id]);
        inventoryCount = parseInt(inventoryRows[0]?.count || 0, 10);
      } else {
        const inventoryRows = await runQuery(`SELECT COUNT(*) as count FROM lasea_inventory WHERE is_active = true`);
        inventoryCount = parseInt(inventoryRows[0]?.count || 0, 10);
      }

      setStats({
        totalClients: clientCount,
        activeItems: inventoryCount
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statsData = [
    {
      id: 1,
      label: 'Total Clients',
      value: loading ? '...' : stats?.totalClients?.toString(),
      icon: 'Users',
      color: 'primary',
      trend: loading ? 'Loading...' : `${stats?.totalClients} registered`
    },
    {
      id: 2,
      label: 'Active Inventory Items',
      value: loading ? '...' : stats?.activeItems?.toString(),
      icon: 'Package',
      color: 'accent',
      trend: selectedClient ? `${selectedClient?.companyName || 'Current client'}` : 'All clients'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: 'bg-primary/10 text-primary border-primary/20',
      accent: 'bg-accent/10 text-accent border-accent/20',
      warning: 'bg-warning/10 text-warning border-warning/20',
      success: 'bg-success/10 text-success border-success/20'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {statsData?.map((stat) => {
        const isInteractive = stat.id === 1 && onTotalClientsClick;
        return (
          <div
            key={stat?.id}
            onClick={isInteractive ? onTotalClientsClick : undefined}
            className={`bg-card rounded-lg p-6 border border-border transition-smooth relative ${isInteractive
              ? 'cursor-pointer hover:border-primary/50 hover:shadow-md active:scale-[0.98]'
              : 'hover:elevation-2'
              }`}
          >
            {isInteractive && (
              <div className="absolute top-4 right-4 text-primary/60 opacity-0 group-hover:opacity-100 transition-opacity">
                <Icon name="ChevronRight" size={20} />
              </div>
            )}
            <div className={`flex items-start justify-between mb-4 ${isInteractive ? 'group' : ''}`}>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${getColorClasses(stat?.color)}`}>
                <Icon name={stat?.icon} size={24} />
              </div>
            </div>
            <div>
              <p className="text-3xl font-semibold text-foreground mb-1">{stat?.value}</p>
              <p className="text-sm font-medium text-muted-foreground mb-2">{stat?.label}</p>
              <p className="text-xs text-muted-foreground">{stat?.trend}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default QuickStatsGrid;